import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { gigSwal } from '../utils/swal';
import io from 'socket.io-client';
import { Music, User, ListMusic, CheckCircle, Users, SlidersHorizontal, X } from 'lucide-react';
import { getDeviceId } from '../utils/deviceId';
import { resolveTheme } from '../constants/themes';
import SOCIAL_PLATFORMS from '../constants/socialIcons';

const API_URL = process.env.REACT_APP_API_URL || '';

function AudienceView() {
  const { bandSlug } = useParams();
  const location = useLocation();
  const cameFromHome = new URLSearchParams(location.search).get('ref') === 'home';
  const [songs, setSongs] = useState([]);
  const [requesterName, setRequesterName] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [deviceLimit, setDeviceLimit] = useState(null);
  const [deviceId] = useState(getDeviceId());
  const [requestingId, setRequestingId] = useState(null);
  const [gigStatus, setGigStatus] = useState(null);
  const [branding, setBranding] = useState(null);
  const [members, setMembers] = useState([]);
  const [suggestionSheetOpen, setSuggestionSheetOpen] = useState(false);
  const [suggestionTitle, setSuggestionTitle] = useState('');
  const [suggestionArtist, setSuggestionArtist] = useState('');
  const [suggestionSent, setSuggestionSent] = useState(false);
  const [requestedSongIds, setRequestedSongIds] = useState([]);
  const [lsKey, setLsKey] = useState(null);

  // Welcome modal
  const [showWelcome, setShowWelcome] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState(['All']);
  const [filterOpen, setFilterOpen] = useState(false);

  // Bottom sheet drag-to-dismiss
  const [sheetY, setSheetY] = useState(0);
  const dragStart = useRef(null);
  const sheetRef = useRef(null);

  // Lock body scroll while filter sheet is open
  useEffect(() => {
    document.body.style.overflow = (filterOpen || suggestionSheetOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [filterOpen, suggestionSheetOpen]);

  useEffect(() => {
    fetchSongs();
    checkDeviceLimit();
    checkGigStatus();
    axios.get(`${API_URL}/api/settings`).then(res => setBranding(res.data)).catch(() => {});
    axios.get(`${API_URL}/api/band-members`).then(res => setMembers(res.data)).catch(() => {});

    const socket = io(API_URL);
    socket.emit('joinGig', bandSlug);

    socket.on('setListUpdated', () => {
      fetchSongs();
    });

    socket.on('requestStatusChanged', (data) => {
      setGigStatus(prev => ({
        ...prev,
        is_accepting_requests: data.is_accepting_requests
      }));
      if (data.is_accepting_requests) {
        setMessage('Requests are now open!');
      } else {
        setMessage('Requests have been closed');
      }
      setTimeout(() => setMessage(''), 3000);
    });

    return () => socket.disconnect();
  }, [bandSlug]);

  const checkGigStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/band/${bandSlug}/status`);
      setGigStatus(response.data);
      if (!cameFromHome) setShowWelcome(true);
      if (response.data.gig_date) {
        const key = `gig_req_${bandSlug}_${response.data.gig_date.toString().slice(0, 10)}`;
        setLsKey(key);
        try { setRequestedSongIds(JSON.parse(localStorage.getItem(key) || '[]')); } catch {}
      }
    } catch (err) {
      console.error('Error checking gig status:', err);
    }
  };

  const dismissWelcome = () => setShowWelcome(false);

  const formatWelcomeDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const fetchSongs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/band/${bandSlug}/songs`);
      setSongs(response.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setMessage('Error loading songs');
      setLoading(false);
    }
  };

  const checkDeviceLimit = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/band/${bandSlug}/can-request?deviceId=${deviceId}`
      );
      setDeviceLimit(response.data);
    } catch (err) {
      console.error('Error checking device limit:', err);
    }
  };

  const handleRequest = async (songId) => {
    if (!requesterName.trim()) {
      gigSwal(theme, {
        title: 'Name Required',
        text: 'Please enter your name before requesting a song',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (gigStatus && !gigStatus.is_accepting_requests) {
      gigSwal(theme, {
        title: 'Requests Closed',
        text: 'This gig is not currently accepting song requests. The musician will open requests when the gig starts.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (deviceLimit && !deviceLimit.canRequest) {
      setMessage(`You've already requested ${deviceLimit.maxRequests} song${deviceLimit.maxRequests > 1 ? 's' : ''}!`);
      return;
    }

    setRequestingId(songId);

    try {
      await axios.post(`${API_URL}/api/band/${bandSlug}/requests`, {
        songId,
        requesterName,
        deviceId,
      });

      await axios.post(`${API_URL}/api/band/${bandSlug}/track-request`, { deviceId });
      await checkDeviceLimit();

      setRequestedSongIds(prev => {
        const updated = [...prev, songId];
        if (lsKey) try { localStorage.setItem(lsKey, JSON.stringify(updated)); } catch {}
        return updated;
      });

      setMessage('Request submitted!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      if (err.response && err.response.status === 403) {
        gigSwal(theme, {
          title: 'Requests Closed',
          text: 'This gig is not currently accepting song requests.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
        checkGigStatus();
      } else if (err.response && err.response.status === 409) {
        setMessage("You've already requested this song!");
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error submitting request');
      }
    } finally {
      setRequestingId(null);
    }
  };

  const handleSuggest = async () => {
    if (!suggestionTitle.trim()) return;
    try {
      await axios.post(`${API_URL}/api/band/${bandSlug}/suggestions`, {
        title: suggestionTitle,
        artist_name: suggestionArtist,
        suggested_by: requesterName,
      });
      setSuggestionSent(true);
      setSuggestionTitle('');
      setSuggestionArtist('');
      setTimeout(() => {
        setSuggestionSent(false);
        setSuggestionSheetOpen(false);
      }, 3000);
    } catch {
      // silent fail
    }
  };

  // Genre filter helpers
  const toggleGenre = (g) => {
    if (g === 'All') { setSelectedGenres(['All']); return; }
    setSelectedGenres(prev => {
      const wo = prev.filter(x => x !== 'All');
      if (wo.includes(g)) {
        const next = wo.filter(x => x !== g);
        return next.length === 0 ? ['All'] : next;
      }
      return [...wo, g];
    });
  };

  // Sheet drag handlers
  const onTouchStart = e => { dragStart.current = e.touches[0].clientY; };
  const onTouchMove = e => {
    const dy = e.touches[0].clientY - dragStart.current;
    if (dy > 0) setSheetY(dy);
  };
  const onTouchEnd = () => {
    if (sheetY > 80) setFilterOpen(false);
    setSheetY(0);
  };

  const uniqueGenres = [...new Set(songs.map(song => song.genre))].sort();
  const isAll = selectedGenres.includes('All');
  const filterLabel = isAll ? null : selectedGenres.join(' · ');

  const filteredSongs = songs.filter(song => {
    if (requestedSongIds.includes(song.id)) return false;
    const gOk = isAll || selectedGenres.includes(song.genre);
    const matchesSearch =
      song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      song.artist_original.toLowerCase().includes(searchTerm.toLowerCase());
    return gOk && matchesSearch;
  });

  if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;

  const hasReachedLimit = deviceLimit && !deviceLimit.canRequest;

  const theme = resolveTheme(branding);
  const primary = theme.primary;
  const headerBg = theme.navGradient;

  // Brand header identity
  const brandName = branding?.band_name || 'GigRequest';
  const brandFontSize =
    brandName.length <= 12 ? 28 :
    brandName.length <= 18 ? 22 :
    brandName.length <= 26 ? 17 : 14;

  const activeSocials = branding?.show_social_media
    ? SOCIAL_PLATFORMS.filter(p => branding?.[p.key])
    : [];

  return (
    <>
      {/* Welcome modal */}
      {showWelcome && gigStatus && (() => {
        const hasSplash = !!branding?.splash_bg_url;
        const textColor = hasSplash ? '#fff' : theme.text;
        const subColor = hasSplash ? 'rgba(255,255,255,0.8)' : theme.textSecondary;
        return (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: hasSplash ? '#000' : theme.isDark
            ? `linear-gradient(160deg, ${theme.background} 0%, ${theme.surface} 100%)`
            : theme.surfaceAlt,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1.5rem',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          overflow: 'hidden',
        }}>
          {hasSplash && (
            <>
              <img
                src={branding.splash_bg_url}
                alt=""
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  objectPosition: `${branding.splash_bg_x ?? 50}% ${branding.splash_bg_y ?? 50}%`,
                  transform: `scale(${(branding.splash_bg_zoom || 100) / 100})`,
                  transformOrigin: 'center center',
                  pointerEvents: 'none',
                }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.52)' }} />
            </>
          )}
          <div style={{ maxWidth: '480px', width: '100%', position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt="Band logo" style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '12px', marginBottom: '0.75rem' }} />
              ) : (
                <div style={{ marginBottom: '0.5rem' }}><Music size={48} color={hasSplash ? '#fff' : theme.primary} /></div>
              )}
              <h1 style={{ color: textColor, fontSize: '2.2rem', fontWeight: '700', margin: '0 0 0.25rem', lineHeight: 1.2, textShadow: hasSplash ? '0 2px 8px rgba(0,0,0,0.5)' : 'none' }}>
                {branding?.band_name || gigStatus.gig_name}
              </h1>
              {branding?.tagline && (
                <p style={{ color: subColor, margin: '0 0 0.5rem', fontSize: '1rem', fontStyle: 'italic' }}>
                  {branding.tagline}
                </p>
              )}
              {(gigStatus.venue_name || gigStatus.gig_date) && (
                <p style={{ color: subColor, margin: '0 0 1rem', fontSize: '0.95rem' }}>
                  {[gigStatus.venue_name, gigStatus.gig_date && formatWelcomeDate(gigStatus.gig_date)].filter(Boolean).join('  ·  ')}
                </p>
              )}
              <span style={{
                display: 'inline-block',
                background: gigStatus.is_accepting_requests ? 'rgba(52,211,153,0.15)' : 'rgba(239,68,68,0.15)',
                border: gigStatus.is_accepting_requests ? '1px solid rgba(52,211,153,0.4)' : '1px solid rgba(239,68,68,0.4)',
                color: gigStatus.is_accepting_requests ? '#34d399' : '#f87171',
                borderRadius: '999px',
                padding: '0.25rem 0.9rem',
                fontSize: '0.85rem',
                fontWeight: '600',
              }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: gigStatus.is_accepting_requests ? '#34d399' : '#f87171', marginRight: '0.4rem', verticalAlign: 'middle' }} />
                {gigStatus.is_accepting_requests ? 'Requests are open' : 'Requests are closed'}
              </span>
            </div>

            <div style={{
              background: hasSplash ? 'rgba(0,0,0,0.35)' : theme.isDark ? 'rgba(255,255,255,0.06)' : theme.surface,
              border: `1px solid ${hasSplash ? 'rgba(255,255,255,0.15)' : theme.isDark ? 'rgba(255,255,255,0.1)' : theme.border}`,
              borderRadius: '16px',
              padding: '1.75rem',
              marginBottom: '1.75rem',
              backdropFilter: hasSplash ? 'blur(12px)' : 'none',
              WebkitBackdropFilter: hasSplash ? 'blur(12px)' : 'none',
            }}>
              <p style={{
                color: theme.textMuted, fontSize: '0.75rem', fontWeight: '700',
                letterSpacing: '0.1em', textTransform: 'uppercase',
                margin: '0 0 1.25rem', textAlign: 'center',
              }}>
                How it works
              </p>
              {[
                [User, 'Enter your name', 'So we know who to shout out!'],
                [ListMusic, 'Browse the song list', 'See everything we can play tonight'],
                [CheckCircle, 'Tap a song to request it', "We'll do our best to play it!"],
              ].map(([StepIcon, title, sub], i) => (
                <div key={i} style={{ display: 'flex', gap: '1rem', marginBottom: i < 2 ? '1rem' : 0 }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: hasSplash ? 'rgba(255,255,255,0.15)' : `${theme.primary}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <StepIcon size={18} color={hasSplash ? '#fff' : theme.primary} />
                  </div>
                  <div>
                    <div style={{ color: textColor, fontWeight: '600', fontSize: '0.95rem' }}>{title}</div>
                    <div style={{ color: subColor, fontSize: '0.82rem', marginTop: '0.1rem' }}>{sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={dismissWelcome}
              style={{
                display: 'block', width: '100%',
                padding: '1.1rem',
                background: headerBg,
                color: theme.onPrimary, border: 'none', borderRadius: '12px',
                fontSize: '1.1rem', fontWeight: '700', cursor: 'pointer',
                boxShadow: `0 4px 24px ${theme.primary}66`,
                minHeight: 52,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              Let's go!
            </button>
          </div>
        </div>
        );
      })()}

      <style>{`
        .gr-input::placeholder { color: ${theme.textMuted}; opacity: 1; }
        .gr-input::-webkit-input-placeholder { color: ${theme.textMuted}; }
        .gr-input::-moz-placeholder { color: ${theme.textMuted}; }
        .gr-footer-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: theme.background,
        color: theme.text,
        WebkitFontSmoothing: 'antialiased',
      }}>

        {/* ── BRAND HEADER ─────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          background: headerBg,
          padding: '14px 16px 12px',
          paddingTop: 'max(14px, calc(14px + env(safe-area-inset-top)))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          minHeight: branding?.promo_photo_url ? 68 : undefined,
        }}>
          {branding?.promo_photo_url && (
            <>
              <img
                src={branding.promo_photo_url}
                alt=""
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  objectPosition: `${branding.header_bg_x ?? 50}% ${branding.header_bg_y ?? 50}%`,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              />
              <div style={{ position: 'absolute', inset: 0, background: headerBg, opacity: 0.78, zIndex: 1 }} />
            </>
          )}
          <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between', gap: 12, position: 'relative', zIndex: 2 }}>
            {/* Logo or scaled band name */}
            {branding?.logo_url ? (
              <img
                src={branding.logo_url}
                alt={brandName}
                style={{ height: 40, maxWidth: 160, objectFit: 'contain' }}
              />
            ) : (
              <div>
                <div style={{
                  fontSize: 10, letterSpacing: 3, textTransform: 'uppercase',
                  fontFamily: 'monospace', color: `${theme.onPrimary}80`,
                  lineHeight: 1, marginBottom: 2,
                }}>
                  Live Set
                </div>
                <div style={{
                  fontSize: brandFontSize,
                  fontWeight: 900,
                  color: theme.onPrimary,
                  lineHeight: 1,
                  letterSpacing: brandName.length > 18 ? 0 : -0.5,
                }}>
                  {brandName}
                </div>
              </div>
            )}

            {/* Requests remaining counter */}
            {deviceLimit && !hasReachedLimit && (
              <div style={{
                background: 'rgba(0,0,0,0.50)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 22,
                padding: '6px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                flexShrink: 0,
              }}>
                <Music size={14} color="#fff" />
                <span style={{
                  color: '#fff',
                  fontSize: 13,
                  fontFamily: 'monospace',
                  whiteSpace: 'nowrap',
                }}>
                  {deviceLimit.remaining} left
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── CONTROLS BAR ─────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          background: theme.isDark ? 'rgba(0,0,0,0.4)' : theme.surfaceAlt,
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          borderBottom: `1px solid ${theme.border}`,
          zIndex: 100,
          boxShadow: theme.isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.07)',
        }}>
          {/* Name — full width, font-size 16 prevents iOS auto-zoom */}
          <input
            type="text"
            placeholder="Your name"
            value={requesterName}
            onChange={(e) => setRequesterName(e.target.value)}
            disabled={hasReachedLimit}
            className="gr-input"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: 10,
              padding: '12px 14px',
              color: theme.text,
              fontSize: 16,
              outline: 'none',
              minHeight: 44,
            }}
          />

          {/* Search + filter button */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder="Search songs or artists..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="gr-input"
              style={{
                flex: 1,
                background: theme.surface,
                border: `1px solid ${theme.border}`,
                borderRadius: 10,
                padding: '0 14px',
                color: theme.text,
                fontSize: 16,
                outline: 'none',
                height: 50,
                boxSizing: 'border-box',
              }}
            />

            <button
              onClick={() => setFilterOpen(true)}
              style={{
                flexShrink: 0,
                background: filterLabel ? primary : theme.surface,
                border: filterLabel ? 'none' : `1px solid ${theme.border}`,
                borderRadius: 10,
                width: 50,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                position: 'relative',
                WebkitTapHighlightColor: 'transparent',
                transition: 'background 0.15s',
                minHeight: 44,
              }}
            >
              <SlidersHorizontal
                size={20}
                color={filterLabel ? theme.onPrimary : theme.textSecondary}
              />
              {filterLabel && (
                <div style={{
                  position: 'absolute',
                  top: 8, right: 8,
                  width: 7, height: 7,
                  borderRadius: '50%',
                  background: theme.onPrimary,
                  opacity: 0.7,
                }} />
              )}
            </button>
          </div>

          {/* Active genre chips */}
          {filterLabel && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedGenres.map(g => (
                <span
                  key={g}
                  onClick={() => toggleGenre(g)}
                  style={{
                    background: `${primary}18`,
                    border: `1px solid ${primary}55`,
                    color: primary,
                    borderRadius: 14,
                    padding: '4px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  {g} <X size={11} style={{ opacity: 0.6 }} />
                </span>
              ))}
              <span
                onClick={() => setSelectedGenres(['All'])}
                style={{ color: theme.textMuted, fontSize: 12, cursor: 'pointer' }}
              >
                Clear
              </span>
            </div>
          )}

          {/* Message feedback */}
          {message && (
            <div style={{
              fontSize: 12,
              color: theme.textSecondary,
              textAlign: 'center',
              background: theme.surface,
              border: `1px solid ${theme.border}`,
              padding: '6px',
              borderRadius: 6,
            }}>
              {message}
            </div>
          )}
        </div>

        {/* ── SONG LIST ────────────────────────────────────── */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '12px',
        }}>
          {/* Requests Closed Banner */}
          {gigStatus && !gigStatus.is_accepting_requests && !hasReachedLimit && (
            <div style={{ borderRadius: '12px', overflow: 'hidden', marginBottom: '1rem', border: '2px solid #dc3545' }}>
              <div style={{ background: '#f8d7da', padding: '0.9rem 1rem', textAlign: 'center' }}>
                <div style={{ color: '#721c24', fontWeight: '700', fontSize: '1rem', marginBottom: '0.2rem' }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f87171', marginRight: '0.4rem', verticalAlign: 'middle' }} />
                  Requests Not Open Yet
                </div>
                <div style={{ color: '#721c24', fontSize: '0.85rem' }}>
                  The musician will open requests when the gig starts.
                </div>
              </div>
              {(activeSocials.length > 0 || members.length > 0) && (
                <div style={{ background: theme.surface, padding: '1rem', textAlign: 'center', borderTop: '1px solid #f5c6cb' }}>
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: theme.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    While you wait
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {members.length > 0 && (
                      <a
                        href={`/gig/${bandSlug}/about`}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.35rem 0.85rem', borderRadius: '999px',
                          border: `1px solid ${primary}`, color: primary,
                          background: theme.surface, textDecoration: 'none',
                          fontSize: '0.8rem', fontWeight: '600', minHeight: 36,
                        }}
                      >
                        <Users size={14} /> Meet the Band
                      </a>
                    )}
                    {activeSocials.map(({ key, label, Icon }) => (
                      <a
                        key={key}
                        href={branding[key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.35rem 0.85rem', borderRadius: '999px',
                          border: `1px solid ${primary}`, color: primary,
                          background: theme.surface, textDecoration: 'none',
                          fontSize: '0.8rem', fontWeight: '600', minHeight: 36,
                        }}
                      >
                        <Icon size={13} /> {label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {hasReachedLimit ? (
            <div style={{
              padding: '2rem',
              background: theme.surface,
              border: `2px solid ${theme.border}`,
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: theme.text }}>
                Request Limit Reached
              </h3>
              <p style={{ margin: 0, color: theme.textSecondary }}>
                You've already requested {deviceLimit.maxRequests} song{deviceLimit.maxRequests > 1 ? 's' : ''} from this device.
                Thank you for your participation!
              </p>
            </div>
          ) : (
            <>
              {(searchTerm || !isAll) && (
                <p style={{ color: theme.textMuted, fontSize: 13, marginBottom: 12, padding: '0 4px' }}>
                  Showing {filteredSongs.length} of {songs.length} songs
                </p>
              )}

              {filteredSongs.length === 0 ? (
                <div style={{
                  padding: '3rem 1rem',
                  textAlign: 'center',
                  background: theme.surface,
                  borderRadius: '12px',
                  color: theme.textSecondary,
                }}>
                  <p>No songs found. Try a different search or filter.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {filteredSongs.map(song => {
                    const closed = gigStatus && !gigStatus.is_accepting_requests;
                    const busy = requestingId === song.id;
                    return (
                      <div key={song.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px',
                        background: theme.surface,
                        borderRadius: 10,
                        boxShadow: theme.isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                        gap: 10,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 600,
                            fontSize: 15,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginBottom: 3,
                          }}>
                            {song.title}
                          </div>
                          <div style={{ color: theme.textSecondary, fontSize: 13 }}>
                            {song.artist_original}
                            <span style={{ color: theme.textMuted, margin: '0 5px' }}>·</span>
                            <span style={{ textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 11, color: theme.textMuted }}>
                              {song.genre}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRequest(song.id)}
                          disabled={busy || closed}
                          style={{
                            flexShrink: 0,
                            background: (busy || closed) ? 'transparent' : primary,
                            color: (busy || closed) ? theme.textMuted : theme.onPrimary,
                            border: (busy || closed) ? `1px solid ${theme.border}` : 'none',
                            borderRadius: 10,
                            padding: '10px 18px',
                            fontWeight: 700,
                            fontSize: 14,
                            cursor: (busy || closed) ? 'default' : 'pointer',
                            minHeight: 44,
                            WebkitTapHighlightColor: 'transparent',
                            transition: 'all 0.2s',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {busy ? '...' : closed ? 'Closed' : 'Request'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

        </div>

        {/* ── FOOTER ───────────────────────────────────────── */}
        <div style={{
          flexShrink: 0,
          background: theme.surface,
          borderTop: `3px solid ${primary}`,
          boxShadow: theme.isDark ? 'none' : '0 -2px 8px rgba(0,0,0,0.08)',
          paddingBottom: 'max(6px, calc(6px + env(safe-area-inset-bottom)))',
        }}>
          <div className="gr-footer-scroll" style={{ overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            <div style={{
              display: 'inline-flex',
              minWidth: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px 4px',
              boxSizing: 'border-box',
            }}>
              {members.length > 0 && (
                <a
                  href={`/gig/${bandSlug}/about`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    padding: '0 12px', borderRadius: '999px', height: 32,
                    border: `1px solid ${primary}`, color: primary,
                    background: theme.surface, textDecoration: 'none',
                    fontSize: '0.8rem', fontWeight: '600', boxSizing: 'border-box',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  <Users size={13} /> Meet the Band
                </a>
              )}
              {activeSocials.map(({ key, label, Icon }) => (
                <a
                  key={key}
                  href={branding[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0 12px', borderRadius: '999px', height: 32,
                    border: `1px solid ${primary}`, color: primary,
                    background: theme.surface, textDecoration: 'none',
                    fontSize: '0.8rem', fontWeight: '600', boxSizing: 'border-box',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  <Icon size={13} /> {label}
                </a>
              ))}
              <button
                onClick={() => setSuggestionSheetOpen(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0 12px', borderRadius: '999px', height: 32,
                  border: `1px solid ${primary}`, color: primary,
                  background: theme.surface, cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: '600', fontFamily: 'inherit',
                  boxSizing: 'border-box', whiteSpace: 'nowrap', flexShrink: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Music size={13} /> Suggest a song
              </button>
            </div>
          </div>
          {branding?.footer_text && (
            <p style={{ margin: '0 0.75rem 2px', fontSize: '0.72rem', color: theme.textMuted, textAlign: 'center' }}>
              {branding.footer_text}
            </p>
          )}
        </div>
      </div>

      {/* ── SUGGESTION BOTTOM SHEET ──────────────────────── */}
      {suggestionSheetOpen && (
        <>
          <div
            onClick={() => setSuggestionSheetOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 200,
              WebkitTapHighlightColor: 'transparent',
            }}
          />
          <div
            style={{
              position: 'fixed',
              bottom: 0, left: 0, right: 0,
              zIndex: 201,
              background: theme.surface,
              borderTop: `2px solid ${primary}`,
              borderRadius: '20px 20px 0 0',
              padding: `0 16px calc(24px + env(safe-area-inset-bottom))`,
              boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
              maxWidth: 600,
              margin: '0 auto',
            }}
          >
            <div style={{
              width: 40, height: 4,
              background: theme.border,
              borderRadius: 2,
              margin: '12px auto 20px',
            }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <div style={{ color: theme.text, fontWeight: 700, fontSize: 18 }}>Suggest a Song</div>
                <div style={{ color: theme.textMuted, fontSize: 12, marginTop: 3 }}>Can't find it? We'll consider it for future sets</div>
              </div>
              <button
                onClick={() => setSuggestionSheetOpen(false)}
                style={{
                  background: theme.surfaceAlt, border: 'none',
                  color: theme.textSecondary, borderRadius: '50%',
                  width: 34, height: 34,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {suggestionSent ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', background: '#d4edda', borderRadius: '12px', color: '#155724', fontWeight: '600', fontSize: '0.95rem' }}>
                Thanks! We'll consider it for future sets.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="Song title *"
                  value={suggestionTitle}
                  onChange={e => setSuggestionTitle(e.target.value)}
                  className="gr-input"
                  style={{ padding: '0.75rem', border: `1px solid ${theme.border}`, borderRadius: '10px', fontSize: 16, boxSizing: 'border-box', width: '100%', background: theme.surface, color: theme.text, outline: 'none' }}
                />
                <input
                  type="text"
                  placeholder="Artist (optional)"
                  value={suggestionArtist}
                  onChange={e => setSuggestionArtist(e.target.value)}
                  className="gr-input"
                  style={{ padding: '0.75rem', border: `1px solid ${theme.border}`, borderRadius: '10px', fontSize: 16, boxSizing: 'border-box', width: '100%', background: theme.surface, color: theme.text, outline: 'none' }}
                />
                <button
                  onClick={handleSuggest}
                  disabled={!suggestionTitle.trim()}
                  style={{
                    marginTop: 4,
                    padding: '15px',
                    background: suggestionTitle.trim() ? primary : theme.surfaceAlt,
                    color: suggestionTitle.trim() ? theme.onPrimary : theme.textMuted,
                    border: 'none', borderRadius: '12px',
                    fontWeight: 700, fontSize: 16,
                    cursor: suggestionTitle.trim() ? 'pointer' : 'not-allowed',
                    minHeight: 52,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  Submit suggestion
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── FILTER BOTTOM SHEET ──────────────────────────── */}
      {filterOpen && (
        <>
          <div
            onClick={() => setFilterOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 200,
              WebkitTapHighlightColor: 'transparent',
            }}
          />
          <div
            ref={sheetRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{
              position: 'fixed',
              bottom: 0, left: 0, right: 0,
              zIndex: 201,
              background: theme.surface,
              borderTop: `2px solid ${primary}`,
              borderRadius: '20px 20px 0 0',
              padding: `0 16px calc(20px + env(safe-area-inset-bottom))`,
              transform: `translateY(${sheetY}px)`,
              transition: sheetY === 0 ? 'transform 0.3s cubic-bezier(0.32,0.72,0,1)' : 'none',
              boxShadow: '0 -8px 40px rgba(0,0,0,0.3)',
              maxWidth: 600,
              margin: '0 auto',
            }}
          >
            {/* Drag handle */}
            <div style={{
              width: 40, height: 4,
              background: theme.border,
              borderRadius: 2,
              margin: '12px auto 20px',
            }} />

            {/* Title row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <div>
                <div style={{ color: theme.text, fontWeight: 700, fontSize: 18 }}>Filter by Genre</div>
                <div style={{ color: theme.textMuted, fontSize: 12, marginTop: 3 }}>
                  {isAll
                    ? 'All genres shown'
                    : `${filteredSongs.length} song${filteredSongs.length !== 1 ? 's' : ''} match`
                  }
                </div>
              </div>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  background: theme.surfaceAlt, border: 'none',
                  color: theme.textSecondary, borderRadius: '50%',
                  width: 34, height: 34,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Genre pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
              {['All', ...uniqueGenres].map(g => (
                <button
                  key={g}
                  onClick={() => toggleGenre(g)}
                  style={{
                    background: selectedGenres.includes(g) ? primary : theme.surfaceAlt,
                    color: selectedGenres.includes(g) ? theme.onPrimary : theme.textSecondary,
                    border: selectedGenres.includes(g) ? 'none' : `1px solid ${theme.border}`,
                    borderRadius: 24,
                    padding: '10px 20px',
                    fontSize: 15,
                    fontWeight: selectedGenres.includes(g) ? 700 : 400,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    minHeight: 44,
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {g}
                </button>
              ))}
            </div>

            {/* Apply */}
            <button
              onClick={() => setFilterOpen(false)}
              style={{
                marginTop: 24,
                width: '100%',
                background: primary,
                color: theme.onPrimary,
                border: 'none',
                borderRadius: 12,
                padding: '15px',
                fontWeight: 700,
                fontSize: 16,
                cursor: 'pointer',
                minHeight: 52,
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: 0.3,
              }}
            >
              {isAll ? 'Show All Songs' : `Show ${filteredSongs.length} Result${filteredSongs.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </>
      )}
    </>
  );
}

export default AudienceView;
